import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { PublicHeader } from "@/components/PublicHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 800 / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const STEP_LABELS = ["Service", "Location & details", "Your details", "Payment"];

export default function Book() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_id: searchParams.get("service") || "",
    address: "",
    details: "",
    photos: [],
    name: "",
    email: "",
    phone: "",
    payment_choice: "deposit",
    customer_estimate: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => toast.error("Could not load services"));
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const service = useMemo(() => services.find((s) => s.id === form.service_id), [services, form.service_id]);
  const depositPct = config?.deposit_percent ?? 30;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const canNext = () => {
    if (step === 1) return !!form.service_id;
    if (step === 2) return form.address.trim().length > 3;
    if (step === 3) return form.name.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.phone.trim().length > 5;
    return true;
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - form.photos.length);
    if (!files.length) return;
    try {
      const urls = await Promise.all(files.map(fileToDataUrl));
      set("photos", [...form.photos, ...urls].slice(0, 3));
    } catch {
      toast.error("Could not read one of those photos");
    }
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.customer_estimate || Number(form.customer_estimate) <= 0) {
      toast.error("Please enter your estimate of the job cost");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", { ...form, customer_estimate: Number(form.customer_estimate) });
      toast.success("Booking received!");
      navigate(`/book/confirmation/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="noise-overlay min-h-screen bg-background" data-testid="booking-page">
      <PublicHeader phoneDisplay={config?.phone_display} phoneTel={config?.phone_tel} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-2">Book a job</h1>
        <p className="text-muted-foreground mb-8">Takes about 2 minutes. We confirm every booking personally.</p>

        <div className="flex items-center gap-2 mb-3 overflow-x-auto" data-testid="booking-steps-indicator">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 shrink-0">
              <span className={`h-7 w-7 rounded-full text-xs font-bold flex items-center justify-center transition-colors ${step > i ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                {i + 1}
              </span>
              <span className={`text-xs font-medium hidden sm:inline ${step > i ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
              {i < STEP_LABELS.length - 1 && <span className="w-6 h-px bg-border" />}
            </div>
          ))}
        </div>
        <p className="sm:hidden text-sm font-medium text-muted-foreground mb-6" data-testid="booking-step-label">
          Step {step} of 4 — {STEP_LABELS[step - 1]}
        </p>
        <div className="hidden sm:block mb-4" />

        <Card className="rounded-2xl border-border shadow-sm">
          <CardContent className="p-6 sm:p-10">
            {step === 1 && (
              <div data-testid="booking-step-service">
                <h2 className="text-xl font-medium mb-6">What do you need done?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((s) => (
                    <button key={s.id} type="button" onClick={() => set("service_id", s.id)} data-testid={`book-service-${s.id}`}
                      className={`text-left p-4 rounded-xl border transition-colors min-h-[48px] ${form.service_id === s.id ? "border-primary bg-secondary" : "border-border bg-white hover:border-primary/50"}`}>
                      <p className="font-medium text-sm">{s.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">From ${s.base_price}{s.unit === "job" ? "" : "/hr"}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="booking-step-location">
                <h2 className="text-xl font-medium mb-6">Where's the job?</h2>
                <Label htmlFor="booking-address" className="text-xs uppercase tracking-[0.2em] font-semibold">Address</Label>
                <Input id="booking-address" data-testid="booking-address-input" placeholder="e.g. 12 Sharp St, Cooma NSW 2630"
                  value={form.address} onChange={(e) => set("address", e.target.value)} className="h-12 mt-2 mb-6 rounded-xl" />
                <Label htmlFor="booking-details" className="text-xs uppercase tracking-[0.2em] font-semibold">Job details & special instructions</Label>
                <Textarea id="booking-details" data-testid="booking-details-input" rows={4}
                  placeholder="Gate code, dog in yard, size of the area, anything we should know..."
                  value={form.details} onChange={(e) => set("details", e.target.value)} className="mt-2 mb-6 rounded-xl" />
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Photos (optional, up to 3)</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative h-20 w-20 rounded-xl overflow-hidden border border-border">
                      <img src={p} alt={`Job upload ${i + 1}`} className="h-full w-full object-cover" />
                      <button type="button" onClick={() => set("photos", form.photos.filter((_, j) => j !== i))}
                        data-testid={`booking-photo-remove-${i}`}
                        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {form.photos.length < 3 && (
                    <label className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                      data-testid="booking-photo-upload">
                      <Camera className="h-5 w-5 text-muted-foreground" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} />
                    </label>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="booking-step-contact">
                <h2 className="text-xl font-medium mb-6">Your details</h2>
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="booking-name" className="text-xs uppercase tracking-[0.2em] font-semibold">Full name</Label>
                    <Input id="booking-name" data-testid="booking-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-12 mt-2 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="booking-email" className="text-xs uppercase tracking-[0.2em] font-semibold">Email</Label>
                    <Input id="booking-email" data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 mt-2 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone" className="text-xs uppercase tracking-[0.2em] font-semibold">Mobile</Label>
                    <Input id="booking-phone" data-testid="booking-phone-input" type="tel" placeholder="04xx xxx xxx" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12 mt-2 rounded-xl" />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && service && (
              <div data-testid="booking-step-payment">
                <h2 className="text-xl font-medium mb-6">Review & payment</h2>
                <div className="bg-secondary rounded-xl p-5 mb-6 text-sm space-y-2" data-testid="booking-review">
                  <p><span className="text-muted-foreground">Service:</span> <strong>{service.name}</strong></p>
                  <p><span className="text-muted-foreground">Timing:</span> <strong>Blake will contact you to lock in a date &amp; time</strong></p>
                  <p><span className="text-muted-foreground">Where:</span> <strong>{form.address}</strong></p>
                  <p><span className="text-muted-foreground">Estimate:</span> <strong>From ${service.base_price}{service.unit === "job" ? "" : "/hr"}</strong> <span className="text-xs text-muted-foreground">(final quote confirmed after booking)</span></p>
                </div>
                <div className="mb-6">
                  <Label htmlFor="booking-estimate" className="text-xs uppercase tracking-[0.2em] font-semibold">Your estimate</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    What do you think this job will cost? Blake will review your job and confirm the final price before you pay anything.
                  </p>
                  <Input id="booking-estimate" data-testid="booking-estimate-input" type="number" min="1" step="1" placeholder="e.g. 120"
                    value={form.customer_estimate} onChange={(e) => set("customer_estimate", e.target.value)} className="h-12 rounded-xl" />
                </div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Payment option (bank transfer)</Label>
                <div className="space-y-3 mt-2">
                  {[
                    { id: "deposit", label: `Pay ${depositPct}% deposit now`, sub: `$${Math.round(service.base_price * depositPct / 100)} by bank transfer to lock in your booking${service.unit === "job" ? "" : " (based on the first hour)"}` },
                    { id: "full", label: "Pay in full now", sub: `$${service.base_price} by bank transfer${service.unit === "job" ? "" : " (first hour, balance settled after the job)"}` },
                    { id: "on_completion", label: "Pay on completion", sub: "Settle up once the job is done" },
                  ].map((opt) => (
                    <button key={opt.id} type="button" onClick={() => set("payment_choice", opt.id)} data-testid={`payment-option-${opt.id}`}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 min-h-[48px] ${form.payment_choice === opt.id ? "border-primary bg-secondary" : "border-border bg-white hover:border-primary/50"}`}>
                      <span className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${form.payment_choice === opt.id ? "border-primary" : "border-border"}`}>
                        {form.payment_choice === opt.id && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                      </span>
                      <span>
                        <span className="block font-medium text-sm">{opt.label}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{opt.sub}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-10">
              {step > 1 ? (
                <Button variant="outline" className="rounded-full" onClick={() => setStep(step - 1)} data-testid="booking-back-button">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
              ) : (
                <Link to="/" data-testid="booking-cancel-link" className="text-sm text-muted-foreground hover:text-foreground self-center transition-colors">
                  Cancel
                </Link>
              )}
              {step < 4 ? (
                <Button className="rounded-full bg-primary hover:bg-[#21421D] text-white transition-colors" disabled={!canNext()}
                  onClick={() => setStep(step + 1)} data-testid="booking-next-button">
                  Next <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button className="rounded-full bg-accent hover:bg-[#B85827] text-white transition-colors" disabled={submitting}
                  onClick={submit} data-testid="booking-submit-button">
                  {submitting ? "Booking..." : "Confirm Booking"} <CheckCircle2 className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
